import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import type {
  ColumnDef,
  OnChangeFn,
  Row,
  RowSelectionState,
} from "@tanstack/react-table";

import { SelectionCheckbox } from "../components/SelectionCheckbox";
import { keysToRowSelection, rowSelectionToKeys } from "../utils";

interface UseTableSelectionOptions<TData> {
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  selectedRowKeys?: string[];
  onRowSelect?: (keys: string[], rows: TData[]) => void;
  data: TData[];
  getRowId: (row: TData) => string;
}

export const useTableSelection = <TData>({
  enableRowSelection,
  selectedRowKeys,
  onRowSelect,
  data,
  getRowId,
}: UseTableSelectionOptions<TData>): {
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  selectionColumn: ColumnDef<TData, unknown> | null;
  enableRowSelection: boolean | ((row: Row<TData>) => boolean);
} => {
  const isControlled = selectedRowKeys !== undefined;
  const isFirstRender = useRef(true);

  const [internalSelection, setInternalSelection] = useState<RowSelectionState>(
    () => (isControlled ? keysToRowSelection(selectedRowKeys) : {})
  );

  const rowSelection = isControlled
    ? keysToRowSelection(selectedRowKeys)
    : internalSelection;

  useEffect(() => {
    if (isControlled) {
      setInternalSelection(keysToRowSelection(selectedRowKeys));
    }
  }, [isControlled, selectedRowKeys]);

  const onRowSelectionChange = useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((previous: RowSelectionState) => RowSelectionState)
    ) => {
      const newSelection =
        typeof updaterOrValue === "function"
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      if (!isControlled) {
        setInternalSelection(newSelection);
      }

      if (onRowSelect) {
        const keys = rowSelectionToKeys(newSelection);
        const selectedRows = data.filter(row => keys.includes(getRowId(row)));
        onRowSelect(keys, selectedRows);
      }
    },
    [rowSelection, isControlled, onRowSelect, data, getRowId]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
  }, []);

  const selectionColumn: ColumnDef<TData, unknown> | null = useMemo(() => {
    if (!enableRowSelection) return null;

    return {
      id: "_selection",
      header: ({ table }) =>
        SelectionCheckbox({
          checked:
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate"),
          onCheckedChange: value => table.toggleAllPageRowsSelected(!!value),
          ariaLabel: "Select all",
        }),
      cell: ({ row }) =>
        SelectionCheckbox({
          checked: row.getIsSelected(),
          onCheckedChange: value => row.toggleSelected(!!value),
          ariaLabel: "Select row",
        }),
      size: 56,
      enableSorting: false,
      enableResizing: false,
    };
  }, [enableRowSelection]);

  return {
    rowSelection,
    onRowSelectionChange,
    selectionColumn,
    enableRowSelection:
      typeof enableRowSelection === "function"
        ? enableRowSelection
        : !!enableRowSelection,
  };
};
